// SPDX-License-Identifier: AGPL-3.0-or-later

import {GuildIcon} from '@app/features/guild/components/popouts/GuildIcon';
import type {Guild} from '@app/features/guild/models/Guild';
import Guilds from '@app/features/guild/state/Guilds';
import {AvatarStack} from '@app/features/ui/avatars/AvatarStack';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import styles from '@app/features/user/components/popouts/UserProfileMutualSummary.module.css';
import type {Profile} from '@app/features/user/models/Profile';
import {User} from '@app/features/user/models/User';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useMemo} from 'react';

const MUTUAL_FRIENDS_COUNT_DESCRIPTOR = msg({
	message: '{count, plural, one {# mutual friend} other {# mutual friends}}',
	comment: 'Row on the small profile card: how many friends the viewer and this person share.',
});
const MUTUAL_COMMUNITIES_COUNT_DESCRIPTOR = msg({
	message: '{count, plural, one {# mutual community} other {# mutual communities}}',
	comment: 'Row on the small profile card: how many communities the viewer and this person share.',
});
const STACKED_FRIENDS = 3;
const STACKED_COMMUNITIES = 3;
const STACKED_ICON_PX = 20;

interface UserProfileMutualSummaryProps {
	profile: Profile;
	onClick: () => void;
}

// Micup: one row on the small card for what two people share; the full profile lists it.
export const UserProfileMutualSummary: React.FC<UserProfileMutualSummaryProps> = observer(({profile, onClick}) => {
	const {i18n} = useLingui();
	const mutualFriends = profile.mutualFriends ?? [];
	const mutualGuilds = profile.mutualGuilds ?? [];
	const communityCount = mutualGuilds.length;
	const stackedFriends = useMemo(
		() => mutualFriends.slice(0, STACKED_FRIENDS).map((friend) => new User(friend)),
		[mutualFriends],
	);
	const stackedCommunities =
		stackedFriends.length > 0
			? []
			: mutualGuilds
					.slice(0, STACKED_COMMUNITIES)
					.map((mutualGuild) => Guilds.getGuild(mutualGuild.id))
					.filter((guild): guild is Guild => guild != null);
	if (mutualFriends.length === 0 && communityCount === 0) return null;
	const counts = [
		mutualFriends.length > 0 ? i18n._(MUTUAL_FRIENDS_COUNT_DESCRIPTOR, {count: mutualFriends.length}) : null,
		communityCount > 0 ? i18n._(MUTUAL_COMMUNITIES_COUNT_DESCRIPTOR, {count: communityCount}) : null,
	].filter((count) => count !== null);
	return (
		<FocusRing offset={-2} data-flx="user.user-profile-mutual-summary.focus-ring">
			<button
				type="button"
				className={styles.summary}
				onClick={onClick}
				data-flx="user.user-profile-mutual-summary.summary.click"
			>
				{stackedFriends.length > 0 && (
					<AvatarStack
						users={stackedFriends}
						size={20}
						maxVisible={STACKED_FRIENDS}
						enableProfileModal={false}
						showTooltips={false}
						data-flx="user.user-profile-mutual-summary.avatar-stack"
					/>
				)}
				{stackedCommunities.length > 0 && (
					<span className={styles.communityStack} data-flx="user.user-profile-mutual-summary.community-stack">
						{stackedCommunities.map((guild) => (
							<GuildIcon
								key={guild.id}
								id={guild.id}
								name={guild.name}
								icon={guild.icon}
								sizePx={STACKED_ICON_PX}
								className={styles.communityIcon}
							/>
						))}
					</span>
				)}
				<span className={styles.counts} data-flx="user.user-profile-mutual-summary.counts">
					{counts.join(' • ')}
				</span>
			</button>
		</FocusRing>
	);
});
